import React, { Dispatch, SetStateAction, useEffect, useMemo } from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import { Helmet } from "@dr.pogodin/react-helmet";

import {
  Alert as MuiAlert,
  Box,
  Card as MuiCard,
  CircularProgress,
  Paper,
  Stack,
} from "@mui/material";
import { Outlet, useOutletContext, useSearchParams } from "react-router-dom";
import { CustomerInfoType } from "./CustomerInfoForm";
import { spacing } from "@mui/system";
import qs from "qs";
import { useGatewayScript } from "./payment-gateways/useGatewayScript";
import { PaymentGateway } from "./payment-gateways/types";
import {
  OneSolomonAlert,
  OneSolomonPageBackground,
  OneSolomonSpinner,
  normalizeCompanyUuid,
  resolveIsCompanyOneSolomons,
  oneSolomonColors,
  writeOneSolomonModeToSession,
  readCompanyUuidFromSession,
  writeCompanyUuidToSession,
} from "./oneSolomonUi";
const Alert = styled(MuiAlert)(spacing);
const CardBase = styled(MuiCard)(spacing);
const Card = styled(CardBase)<{ $oneSolomon?: boolean }>`
  ${({ $oneSolomon }) =>
    $oneSolomon &&
    css`
      background: transparent;
      box-shadow: none;
    `}
`;

const Wrapper = styled(Paper)<{ $oneSolomon?: boolean }>`
  padding: ${(props) => props.theme.spacing(6)};
  margin-top: ${(props) => props.theme.spacing(20)};

  ${(props) => props.theme.breakpoints.up("md")} {
    padding: ${(props) => props.theme.spacing(10)};
  }

  ${({ $oneSolomon }) =>
    $oneSolomon &&
    css`
      background: ${oneSolomonColors.oldWhite};
      box-shadow: none;
      margin-top: 0;
      padding: 56px 24px;
      min-height: 100vh;
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: center;

      @media (max-width: 640px) {
        padding: 24px 16px;
      }
    `}
`;

export type CustomerPaymentInfoType = CustomerInfoType & {
  amount?: number;
  authKey?: string; // bpoint specific
  testMode?: boolean;
  testModeReason?: string;
  paymentGateway?: PaymentGateway;
  transaction_uuid?: string;
};

function Order() {
  // Load payment gateway script and get gateway configuration
  const { paymentGateway, gatewayConfig, status } = useGatewayScript();

  const [searchParams] = useSearchParams();
  const query = useMemo(
    () => qs.parse(searchParams.toString()),
    [searchParams]
  );
  const initialValuesFromMain = useMemo(
    () => query?.initialValues as any,
    [query]
  );

  const companyUuidFromSearch = useMemo(() => {
    return normalizeCompanyUuid(query?.company_uuid);
  }, [query]);

  const companyUuidFromInitialValues = useMemo(
    () => normalizeCompanyUuid(initialValuesFromMain?.company_uuid),
    [initialValuesFromMain?.company_uuid]
  );

  /**
   * A fresh initialValues payload is authoritative. Only gateway return/reload
   * loads without initialValues may fall back to the sticky session UUID. This
   * prevents a normal order opened after a One Solomon order from inheriting
   * the previous session's company_uuid and being white-labeled incorrectly.
   */
  const companyUuidForCurrentLoad = useMemo(
    () =>
      initialValuesFromMain
        ? companyUuidFromInitialValues
        : companyUuidFromSearch ?? readCompanyUuidFromSession(),
    [
      initialValuesFromMain,
      companyUuidFromInitialValues,
      companyUuidFromSearch,
    ]
  );

  const isCompanyOneSolomonsFromQuery = useMemo(() => {
    if (initialValuesFromMain) {
      return resolveIsCompanyOneSolomons(companyUuidFromInitialValues);
    }

    return resolveIsCompanyOneSolomons(
      companyUuidForCurrentLoad,
      query?.isCompanyOneSolomons as string | boolean | undefined
    );
  }, [
    query,
    initialValuesFromMain,
    companyUuidFromInitialValues,
    companyUuidForCurrentLoad,
  ]);

  const [values, setValues] = React.useState<CustomerPaymentInfoType>(() => {
    if (initialValuesFromMain) {
      // we have to do it like this
      // because the testMode response is sometimes boolean or string "true" or "false"
      const testMode =
        initialValuesFromMain?.testMode?.toString().toLowerCase() === "true";

      // normalize amount to number since URL params come in as strings
      // handle null, undefined, and empty string, but allow 0 as a valid amount
      const amount =
        initialValuesFromMain?.amount != null &&
        initialValuesFromMain?.amount !== ""
          ? Number(initialValuesFromMain.amount)
          : undefined;

      return {
        ...initialValuesFromMain,
        company_uuid: companyUuidFromInitialValues,
        testMode,
        amount,
        paymentGateway,
        isCompanyOneSolomons: resolveIsCompanyOneSolomons(
          companyUuidFromInitialValues
        ),
      };
    }

    return {
      firstName: "",
      lastName: "",
      email: "",
      merchantReference: "",
      totalQuoteAmount: 0,
      paymentMethod: "credit-card",
      transaction_for: "deposit",
      paymentGateway,
      company_uuid: companyUuidForCurrentLoad,
      isCompanyOneSolomons: resolveIsCompanyOneSolomons(
        companyUuidForCurrentLoad,
        query?.isCompanyOneSolomons as string | boolean | undefined
      ),
    };
  });

  // Keep the render decision aligned with the source-selection rule above:
  // when a new initialValues payload exists, ignore stale in-memory/session UUIDs.
  const activeCompanyUuid = initialValuesFromMain
    ? companyUuidFromInitialValues
    : values.company_uuid ?? companyUuidForCurrentLoad;
  const isCompanyOneSolomons = resolveIsCompanyOneSolomons(
    activeCompanyUuid,
    initialValuesFromMain ? undefined : values.isCompanyOneSolomons,
    isCompanyOneSolomonsFromQuery
  );

  // Keep the layout's One Solomon detection working after navigate() drops the
  // query string — Layout reads this flag back from sessionStorage.
  useEffect(() => {
    writeOneSolomonModeToSession(isCompanyOneSolomons);
  }, [isCompanyOneSolomons]);

  // Make company_uuid sticky so a hard reload without URL params still
  // white-labels and downstream forms can resolve it from customerInfo. When a
  // fresh initialValues payload omits company_uuid, this intentionally writes
  // undefined, clearing any stale One Solomon session data.
  const stickyCompanyUuid = initialValuesFromMain
    ? companyUuidFromInitialValues
    : values.company_uuid ?? companyUuidForCurrentLoad;
  useEffect(() => {
    writeCompanyUuidToSession(stickyCompanyUuid);
  }, [stickyCompanyUuid]);

  return (
    <React.Fragment>
      {isCompanyOneSolomons && <OneSolomonPageBackground />}
      <Wrapper $oneSolomon={isCompanyOneSolomons}>
        <Helmet>
          <title>
            {isCompanyOneSolomons ? "Confirm Order — ONESOLOMON" : "Accept"}
          </title>
        </Helmet>
        {status === "ready" ? (
          <Card my={isCompanyOneSolomons ? 0 : 6} $oneSolomon={isCompanyOneSolomons}>
            {!isCompanyOneSolomons &&
              (values?.transaction_for !== "deposit" ||
                paymentGateway === "sawfish") &&
              !!values?.company_image && (
                <img src={values.company_image} alt="logo" width="100%" />
              )}
            <Outlet context={{ values, setValues, initialValuesFromMain }} />
          </Card>
        ) : status === "error" ? (
          <Card my={isCompanyOneSolomons ? 0 : 6} $oneSolomon={isCompanyOneSolomons}>
            {isCompanyOneSolomons ? (
              <OneSolomonAlert variant="error">
                Sorry, this service is not available at this time. We are aware
                that some of our customers are currently experiencing issues
                processing online payments via {gatewayConfig.name}. This is
                due to technical issues experienced by our payment provider
                and we are working to resolve this as soon as possible. Thank
                you for your patience.
              </OneSolomonAlert>
            ) : (
              <Stack
                direction="column"
                spacing={6}
                justifyContent="space-between"
                alignItems="center"
              >
                <img src="/icon128x128.svg" alt="logo" width="30%" />
                <Alert severity="error">
                  Sorry this service is not available at this time and are aware
                  that some of our customers are currently experiencing issues
                  processing online payments via {gatewayConfig.name}. <br />
                  <br />
                  This is due to technical issues experienced by our payment
                  provider and we are working to resolve this as soon as
                  possible. <br />
                  <br />
                  Thank you for your patience.
                </Alert>
              </Stack>
            )}
          </Card>
        ) : isCompanyOneSolomons ? (
          <Box display="flex" justifyContent="center" my={6}>
            <OneSolomonSpinner />
          </Box>
        ) : (
          <Box display="flex" justifyContent="center" my={6}>
            <CircularProgress />
          </Box>
        )}
      </Wrapper>
    </React.Fragment>
  );
}

export default Order;

type ContextType = {
  values?: CustomerPaymentInfoType;
  setValues: Dispatch<SetStateAction<CustomerPaymentInfoType>>;
  initialValuesFromMain?: Partial<CustomerPaymentInfoType>;
};

export function useCustomerPaymentInfo() {
  return useOutletContext<ContextType>();
}
